import { useQuery, UseQueryResult } from '@tanstack/react-query';
import useAuthReady from '@core/hooks/useAuthReady';
import { UserOrganizationModel } from '@core/types';
import getUserOrganizations from '../api/getUserOrganizations';

export type UseUserOrganizationsResult = Omit<
  UseQueryResult<UserOrganizationModel[], any>,
  'data'
> & { organizations: UserOrganizationModel[] };

const QUERY_KEY = 'userOrganizations';

const useUserOrganizations = (userUid = ''): UseUserOrganizationsResult => {
  const { isUserReady } = useAuthReady();

  const { data: organizations = [], ...rest } = useQuery({
    queryFn: () => getUserOrganizations(),
    // `userUid` is not a request parameter (getUserOrganizations takes none),
    // but it must stay in the key: useLogout clears MSAL and the product token
    // without calling queryClient.clear(), so a shared key would serve one
    // account's organization list to the next one signed in on this tab.
    queryKey: [QUERY_KEY, userUid],
    // Reached from useProfile, i.e. ~55 call sites on every page. Without the
    // guard this fired at mount under the `''` key and again once idTokenClaims
    // resolved — two identical requests, the first of them pre-auth.
    enabled: isUserReady && !!userUid,
    staleTime: 1000 * 60 * 60 * 12,
    gcTime: 1000 * 60 * 60 * 12.5,
  });

  return { organizations, ...rest };
};

export default useUserOrganizations;
